/**
 * arbi-dev TopBar. Stripped-down version of arbi root's TopBar:
 *   KEEP    : logo + DEV badge, sidebar collapse toggle, theme toggle,
 *             density toggle, sign-out
 *   REMOVE  : search, credit/cash badges, undo/redo/refresh, notification
 *             bell + panel, activity ticker, ISG bolt, distributor selector
 *
 * The "DEV" label next to the logo is the at-a-glance differentiator that
 * makes accidentally typing into the wrong tab very hard. Combined with
 * the per-page tab title via useDocumentTitle (`Arbi Dev` on home, e.g.
 * `Feedback Inbox — Arbi Dev` on /admin/feedback per the locked naming
 * convention 2026-05-06), the DEV badge is the single in-app visual
 * differentiator — favicon and other brand assets are canonical-synced
 * to arbi-root per the brand-asset policy.
 */
import { type JSX } from 'react';
import {
  Sun,
  Moon,
  AlignJustify,
  AlignLeft,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';

export default function TopBar(): JSX.Element {
  const {
    theme,
    toggleTheme,
    density,
    toggleDensity,
    sidebarCollapsed,
    setSidebarCollapsed,
    signOut,
  } = useApp();

  return (
    <header
      className="topbar h-[52px] flex-shrink-0 bg-topbar border-b border-border flex items-center justify-between px-3"
      style={{ zIndex: 'var(--z-sticky, 10)' }}
    >
      {/* LEFT — sidebar toggle + arbi-dev wordmark */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1.5 rounded text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>

        <div className="flex items-center gap-2 select-none">
          <span className="text-sm font-semibold text-ink-bright tracking-tight">arbi</span>
          <span
            className={clsx(
              'text-2xs font-mono font-medium uppercase tracking-wider px-1.5 py-0.5 rounded',
              'bg-warning/20 text-warning border border-warning/30'
            )}
          >
            DEV
          </span>
        </div>
      </div>

      {/* RIGHT — theme, density, sign-out */}
      <div className="flex items-center gap-1">
        <button
          onClick={toggleDensity}
          className="p-1.5 rounded text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
          aria-label={'Density: ' + density}
          title={'Density: ' + density}
        >
          {density === 'compact' ? <AlignJustify size={18} /> : <AlignLeft size={18} />}
        </button>

        <button
          onClick={toggleTheme}
          className="p-1.5 rounded text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
          aria-label={'Theme: ' + theme}
          title={'Theme: ' + theme}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <button
          onClick={() => {
            void signOut();
          }}
          className="p-1.5 rounded text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={18} />
        </button>
      </div>
    </header>
  );
}
